import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Download, File as FileIcon, Image as ImageIcon, Loader2, History, Sparkles, AlertTriangle, Trash2, Pencil, Eye, Pin, LayoutGrid, List } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { canEditEntry } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DocPreviewDialog, canPreview } from '@/components/app/DocPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEW_STORAGE_KEY = 'av-ged-view';
const CATEGORY_LABEL = {
    template: 'Modèle', meeting_report: 'Réunion', courrier: 'Courrier',
    contract: 'Contrat', invoice: 'Facture', report: 'Rapport',
    justificatif: 'Justificatif', autre: 'Autre',
};
const CLASSABLE = ['contract', 'invoice', 'courrier', 'meeting_report', 'report', 'justificatif', 'autre'];
const isPinned = (d) => Array.isArray(d.tags) && d.tags.includes('pinned');

const fmtSize = (b) => {
    if (!b) return '—';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0; while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
    return `${b.toFixed(b >= 10 ? 0 : 1)} ${u[i]}`;
};

export default function ProjectGED({ projectId, workspaceId }) {
    const { user, session, profile } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const { t, lang } = useLang();
    const inputRef = useRef(null);
    const versionInputRef = useRef(null);
    const [docs, setDocs] = useState([]);
    const [versions, setVersions] = useState({});  // doc_id -> versions[]
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [versioningDocId, setVersioningDocId] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(null);  // doc_id or null
    const [missing, setMissing] = useState(null);
    const [detecting, setDetecting] = useState(false);
    const [view, setView] = useState(() => {
        try { return localStorage.getItem(VIEW_STORAGE_KEY) || 'list'; } catch { return 'list'; }
    });
    const [thumbsByPath, setThumbsByPath] = useState({});
    const [catFilter, setCatFilter] = useState('all');
    const [preview, setPreview] = useState(null);
    const visibleDocs = (catFilter === 'all' ? docs
        : catFilter === 'none' ? docs.filter(d => !d.category)
        : docs.filter(d => d.category === catFilter))
        .slice().sort((a, b) => (isPinned(b) ? 1 : 0) - (isPinned(a) ? 1 : 0));

    useEffect(() => {
        try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignore */ }
    }, [view]);

    const load = async () => {
        setLoading(true);
        const { data, error: selErr } = await supabase.from('documents').select('*').eq('project_id', projectId).or('category.is.null,category.neq.template').order('created_at', { ascending: false });
        if (selErr) {
            setError(`Chargement des documents impossible : ${selErr.message}`);
            toast.error('Chargement des documents impossible — réessayez.');
            setLoading(false);
            return;
        }
        const docs = data || [];
        setDocs(docs);
        setLoading(false);
        // Pre-generate thumbnails (signed URLs) for image docs — used only by grid view
        const imgDocs = docs.filter(d => (d.mime_type || '').startsWith('image/'));
        const thumbs = {};
        await Promise.all(imgDocs.slice(0, 60).map(async (d) => {
            const { data: signed } = await supabase.storage.from('documents').createSignedUrl(d.storage_path, 600);
            if (signed?.signedUrl) thumbs[d.storage_path] = signed.signedUrl;
        }));
        setThumbsByPath(thumbs);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const loadVersions = async (docId) => {
        const { data } = await supabase.from('document_versions').select('*').eq('document_id', docId).order('version', { ascending: false });
        setVersions(v => ({ ...v, [docId]: data || [] }));
    };

    const onFile = async (e) => {
        setError(null);
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) { setError('Fichier > 25 MB'); toast.error('Fichier > 25 MB'); return; }
        setUploading(true);
        const path = `${workspaceId}/${projectId}/${Date.now()}_${file.name}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { cacheControl: '3600', upsert: false });
        if (upErr) {
            setError(upErr.message);
            toast.error(`Upload échoué : ${upErr.message}`);
            setUploading(false);
            return;
        }
        const { data: inserted, error: insErr } = await supabase.from('documents').insert({
            workspace_id: workspaceId, project_id: projectId, name: file.name,
            storage_path: path, mime_type: file.type, size_bytes: file.size,
            uploaded_by: user?.id, status: 'draft',
            created_by: user?.id,
        }).select().single();
        if (insErr) {
            setError(insErr.message);
            toast.error(`Enregistrement échoué : ${insErr.message}`);
        } else {
            if (inserted) setDocs(prev => [inserted, ...prev]);
            await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'document.uploaded', entity_type: 'document', new_value: { name: file.name, path } });
            toast.success('Document téléversé');
        }
        setUploading(false); inputRef.current.value = ''; load();
    };

    const onNewVersion = async (e) => {
        setError(null);
        const file = e.target.files?.[0];
        if (!file || !versioningDocId) return;
        const doc = docs.find(d => d.id === versioningDocId);
        const path = `${workspaceId}/${projectId}/v_${Date.now()}_${file.name}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file);
        if (upErr) { setError(upErr.message); return; }
        const existing = versions[versioningDocId] || [];
        const nextVer = (existing[0]?.version || 0) + 1;
        await supabase.from('document_versions').insert({
            workspace_id: workspaceId, document_id: versioningDocId, version: nextVer,
            storage_path: path, uploaded_by: user.id,
        });
        // Update main doc to point to new file
        await supabase.from('documents').update({ storage_path: path, size_bytes: file.size }).eq('id', versioningDocId);
        await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'document.versioned', entity_type: 'document', new_value: { document_id: versioningDocId, version: nextVer } });
        versionInputRef.current.value = '';
        setVersioningDocId(null);
        load();
    };

    const triggerNewVersion = (docId) => {
        setVersioningDocId(docId);
        setTimeout(() => versionInputRef.current?.click(), 50);
    };

    const showHistory = async (docId) => {
        if (!versions[docId]) await loadVersions(docId);
        setHistoryOpen(docId);
    };

    const download = async (storage_path) => {
        const { data } = await supabase.storage.from('documents').createSignedUrl(storage_path, 300);
        if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    };

    const detect = async () => {
        setDetecting(true); setMissing(null);
        try {
            const { data } = await axios.post(`${API}/ai/detect-missing`,
                { project_id: projectId, workspace_id: workspaceId, language: lang },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            setMissing(data.items || []);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setDetecting(false);
    };

    const classify = async (d, cat) => {
        const value = cat === '' ? null : cat;
        const { error } = await supabase.from('documents').update({ category: value }).eq('id', d.id);
        if (error) { toast.error(`Classement impossible : ${error.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, category: value } : x));
        await supabase.from('audit_logs').insert({
            workspace_id: workspaceId, user_id: user?.id, action: 'document.classified',
            entity_type: 'document', entity_id: d.id, new_value: { name: d.name, category: value },
        });
        toast.success(value ? `Classé : ${CATEGORY_LABEL[value]}` : 'Classement retiré');
    };

    const togglePin = async (d) => {
        const tags = Array.isArray(d.tags) ? d.tags : [];
        const pinned = tags.includes('pinned');
        const newTags = pinned ? tags.filter(t => t !== 'pinned') : [...tags, 'pinned'];
        const { error: upErr } = await supabase.from('documents').update({ tags: newTags }).eq('id', d.id);
        if (upErr) { toast.error(`Épinglage impossible : ${upErr.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, tags: newTags } : x));
        toast.success(pinned ? 'Document désépinglé' : 'Document épinglé en tête de liste');
    };

    const renameDoc = async (d) => {
        const input = window.prompt('Nouveau nom du document :', d.name);
        if (!input || !input.trim() || input.trim() === d.name) return;
        const name = input.trim();
        const { error: upErr } = await supabase.from('documents').update({ name }).eq('id', d.id);
        if (upErr) { toast.error(`Renommage impossible : ${upErr.message}`); return; }
        setDocs(prev => prev.map(x => x.id === d.id ? { ...x, name } : x));
        await supabase.from('audit_logs').insert({
            workspace_id: workspaceId, user_id: user?.id, action: 'document.renamed',
            entity_type: 'document', entity_id: d.id, old_value: { name: d.name }, new_value: { name },
        });
        toast.success('Document renommé');
    };

    const removeDoc = async (d) => {
        if (!window.confirm(`Supprimer le document « ${d.name} » ? Cette action est irréversible.`)) return;
        // Try to delete the storage object first (best effort: ignore errors)
        try { await supabase.storage.from('documents').remove([d.storage_path]); } catch {}
        const { error } = await supabase.from('documents').delete().eq('id', d.id);
        if (error) { toast.error("Vous n'êtes pas autorisé·e à supprimer ce document."); return; }
        await supabase.from('audit_logs').insert({
            workspace_id: workspaceId, user_id: user?.id, action: 'document.deleted',
            entity_type: 'document', entity_id: d.id, old_value: { name: d.name },
        });
        toast.success('Document supprimé');
        load();
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="text-sm text-muted-foreground">{docs.length} document(s)</div>
                    <div className="flex items-center gap-0.5 bg-surface-muted rounded-md p-0.5" data-testid="ged-view-toggle">
                        <button
                            type="button"
                            onClick={() => setView('list')}
                            className={`h-7 w-7 rounded flex items-center justify-center text-xs transition-colors ${view === 'list' ? 'bg-white shadow-sm text-ink' : 'text-muted-foreground hover:text-ink'}`}
                            data-testid="ged-view-list"
                            title="Vue liste"
                        ><List size={14} /></button>
                        <button
                            type="button"
                            onClick={() => setView('grid')}
                            className={`h-7 w-7 rounded flex items-center justify-center text-xs transition-colors ${view === 'grid' ? 'bg-white shadow-sm text-ink' : 'text-muted-foreground hover:text-ink'}`}
                            data-testid="ged-view-grid"
                            title="Vue grille"
                        ><LayoutGrid size={14} /></button>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button onClick={detect} disabled={detecting} variant="outline" size="sm" className="border-secondary text-secondary hover:bg-secondary-light" data-testid="ged-detect-missing">
                        {detecting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-1.5" />}
                        Détecter pièces manquantes
                    </Button>
                    <input ref={inputRef} type="file" className="sr-only" onChange={onFile} data-testid="ged-file-input" />
                    <input ref={versionInputRef} type="file" className="sr-only" onChange={onNewVersion} data-testid="ged-version-input" />
                    <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-primary hover:bg-primary-hover" size="sm" data-testid="ged-upload-btn">
                        {uploading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-1.5" />} {t('ged.upload')}
                    </Button>
                </div>
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md" data-testid="ged-error">{error}</div>}

            {docs.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="ged-category-filter">
                    <button type="button" onClick={() => setCatFilter('all')}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                        data-testid="ged-filter-all">Tous ({docs.length})</button>
                    {CLASSABLE.filter(c => docs.some(d => d.category === c)).map(c => (
                        <button key={c} type="button" onClick={() => setCatFilter(c)}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === c ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                            data-testid={`ged-filter-${c}`}>{CATEGORY_LABEL[c]} ({docs.filter(d => d.category === c).length})</button>
                    ))}
                    {docs.some(d => !d.category) && (
                        <button type="button" onClick={() => setCatFilter('none')}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${catFilter === 'none' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground/70 hover:border-primary/40'}`}
                            data-testid="ged-filter-none">Non classés ({docs.filter(d => !d.category).length})</button>
                    )}
                </div>
            )}

            {missing && missing.length > 0 && (
                <div className="card-soft p-5 border-l-4 border-primary" data-testid="ged-missing-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-primary" />
                        <h3 className="font-heading text-lg">Pièces manquantes détectées par l'IA</h3>
                    </div>
                    <ul className="space-y-2">
                        {missing.map((m, i) => (
                            <li key={i} className="text-sm flex items-start gap-3" data-testid={`missing-item-${i}`}>
                                <span className={`phase-badge ${m.severity === 'high' ? 'bg-destructive/10 text-destructive' : m.severity === 'medium' ? 'bg-primary-light text-primary' : 'bg-secondary-light text-secondary'} mt-0.5 flex-shrink-0`}>{m.severity}</span>
                                <span><strong>{m.piece}</strong> — <span className="text-foreground/70">{m.reason}</span></span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {missing && missing.length === 0 && (
                <div className="card-soft p-5 text-sm text-secondary" data-testid="ged-missing-empty">Aucune pièce manquante détectée. Le dossier semble complet.</div>
            )}

            <div className="card-soft overflow-hidden" data-testid={`ged-${view}-view`}>
                {view === 'grid' ? (
                    <div className="p-5">
                        {loading && (
                            <div className="text-center py-10 text-muted-foreground">
                                <Loader2 className="inline animate-spin mr-2" size={14} />
                            </div>
                        )}
                        {!loading && docs.length === 0 && (
                            <div className="text-center py-10 text-foreground/60" data-testid="ged-grid-empty">{t('ged.empty')}</div>
                        )}
                        {!loading && docs.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                {visibleDocs.map(d => {
                                    const editable = canEditEntry({ entry: d, userId: user?.id, profile, currentWorkspace });
                                    const isImage = (d.mime_type || '').startsWith('image/');
                                    const thumb = thumbsByPath[d.storage_path];
                                    return (
                                        <div
                                            key={d.id}
                                            className="group rounded-xl border border-border bg-white overflow-hidden hover:border-primary/40 transition-colors flex flex-col"
                                            data-testid={`doc-card-${d.id}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => download(d.storage_path)}
                                                className="aspect-square bg-surface-muted/60 flex items-center justify-center overflow-hidden"
                                            >
                                                {isImage && thumb ? (
                                                    <img src={thumb} alt={d.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="flex flex-col items-center text-muted-foreground gap-1.5">
                                                        {isImage ? <ImageIcon size={32} strokeWidth={1.4} /> : <FileIcon size={32} strokeWidth={1.4} />}
                                                        <span className="text-[10px] uppercase tracking-wider">
                                                            {(d.mime_type || '').split('/')[1] || 'fichier'}
                                                        </span>
                                                    </div>
                                                )}
                                            </button>
                                            <div className="p-3 flex-1 flex flex-col">
                                                <div className="font-medium text-xs text-ink line-clamp-2 leading-snug mb-1" title={d.name}>{d.name}</div>
                                                <select value={d.category || ''} onChange={(e) => classify(d, e.target.value)}
                                                    className={`h-6 px-1 rounded text-[10px] border self-start mb-1.5 ${d.category ? 'border-secondary/40 bg-secondary-light text-secondary' : 'border-border bg-background text-muted-foreground'}`}
                                                    data-testid={`doc-category-${d.id}-grid`} title="Classer le document">
                                                    <option value="">Classer…</option>
                                                    {CLASSABLE.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                                                </select>
                                                <div className="text-[10px] text-muted-foreground mt-auto flex items-center justify-between gap-2">
                                                    <span>{fmtSize(d.size_bytes)}</span>
                                                    <span>{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
                                                </div>
                                            </div>
                                            <div className="px-2 pb-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button onClick={() => togglePin(d)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-pin-${d.id}-grid`} title={isPinned(d) ? 'Désépingler' : 'Épingler'}>
                                                    <Pin size={12} className={isPinned(d) ? 'text-amber-600 fill-amber-500' : ''} />
                                                </Button>
                                                {canPreview(d.mime_type, d.name) && (
                                                    <Button onClick={() => setPreview(d)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-preview-${d.id}-grid`} title="Aperçu"><Eye size={12} /></Button>
                                                )}
                                                <Button onClick={() => showHistory(d.id)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-history-${d.id}-grid`}><History size={12} /></Button>
                                                {editable && (
                                                    <Button onClick={() => triggerNewVersion(d.id)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-new-version-${d.id}-grid`}><Upload size={12} /></Button>
                                                )}
                                                <Button onClick={() => download(d.storage_path)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-download-${d.id}-grid`}><Download size={12} /></Button>
                                                {editable && (
                                                    <Button onClick={() => renameDoc(d)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-rename-${d.id}-grid`} title="Renommer"><Pencil size={12} /></Button>
                                                )}
                                                {editable && (
                                                    <Button onClick={() => removeDoc(d)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`doc-delete-${d.id}-grid`}><Trash2 size={12} className="text-destructive" /></Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <table className="w-full">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-6 py-3">{t('ged.name')}</th><th className="px-6 py-3">Catégorie</th><th className="px-6 py-3">{t('ged.size')}</th><th className="px-6 py-3">{t('ged.uploaded_at')}</th><th className="px-6 py-3"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading && <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /></td></tr>}
                        {!loading && docs.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-foreground/60" data-testid="ged-empty">{t('ged.empty')}</td></tr>}
                        {!loading && visibleDocs.map((d) => {
                            const editable = canEditEntry({ entry: d, userId: user?.id, profile, currentWorkspace });
                            return (
                                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                                    <td className="px-6 py-3 font-medium">
                                        <div className="flex items-center gap-2">
                                            {isPinned(d) ? <Pin size={14} className="text-amber-600 fill-amber-500" /> : <FileIcon size={14} className="text-muted-foreground" />} {d.name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3">
                                        <select value={d.category || ''} onChange={(e) => classify(d, e.target.value)}
                                            className={`h-7 px-1.5 rounded-md border text-xs ${d.category ? 'border-secondary/40 bg-secondary-light text-secondary' : 'border-border bg-background text-muted-foreground'}`}
                                            data-testid={`doc-category-${d.id}`} title="Classer le document">
                                            <option value="">Classer…</option>
                                            {CLASSABLE.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-6 py-3 text-sm">{fmtSize(d.size_bytes)}</td>
                                    <td className="px-6 py-3 text-sm">{new Date(d.created_at).toLocaleString()}</td>
                                    <td className="px-6 py-3 text-right">
                                        <Button onClick={() => togglePin(d)} size="sm" variant="ghost" data-testid={`doc-pin-${d.id}`} title={isPinned(d) ? 'Désépingler' : 'Épingler en tête'}>
                                            <Pin size={14} className={isPinned(d) ? 'text-amber-600 fill-amber-500' : ''} />
                                        </Button>
                                        {canPreview(d.mime_type, d.name) && (
                                            <Button onClick={() => setPreview(d)} size="sm" variant="ghost" data-testid={`doc-preview-${d.id}`} title="Aperçu"><Eye size={14} /></Button>
                                        )}
                                        <Button onClick={() => showHistory(d.id)} size="sm" variant="ghost" data-testid={`doc-history-${d.id}`}><History size={14} className="mr-1" /></Button>
                                        {editable && (
                                            <Button onClick={() => triggerNewVersion(d.id)} size="sm" variant="ghost" data-testid={`doc-new-version-${d.id}`}><Upload size={14} className="mr-1" /> v+</Button>
                                        )}
                                        <Button onClick={() => download(d.storage_path)} size="sm" variant="ghost" data-testid={`doc-download-${d.id}`}><Download size={14} className="mr-1.5" /> {t('ged.download')}</Button>
                                        {editable && (
                                            <Button onClick={() => renameDoc(d)} size="sm" variant="ghost" data-testid={`doc-rename-${d.id}`} title="Renommer"><Pencil size={13} /></Button>
                                        )}
                                        {editable && (
                                            <Button onClick={() => removeDoc(d)} size="sm" variant="ghost" data-testid={`doc-delete-${d.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                )}
            </div>

            <DocPreviewDialog doc={preview} onClose={() => setPreview(null)} onDownload={download} />

            <Dialog open={!!historyOpen} onOpenChange={(o) => !o && setHistoryOpen(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Historique des versions</DialogTitle></DialogHeader>
                    {(versions[historyOpen] || []).length === 0 ? (
                        <p className="text-foreground/60 py-6 text-center text-sm">Aucune version antérieure.</p>
                    ) : (
                        <div className="divide-y divide-border">
                            {(versions[historyOpen] || []).map(v => (
                                <div key={v.id} className="flex items-center justify-between py-3" data-testid={`version-${v.id}`}>
                                    <div>
                                        <div className="font-medium">Version {v.version}</div>
                                        <div className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleString()}</div>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => download(v.storage_path)}><Download size={14} className="mr-1.5" /> Télécharger</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
